import { useId, useState, type ReactNode } from 'react';
import { cx } from '../lib/cx';

export interface AccordionItemProps {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  meta?: ReactNode;
}

export function AccordionItem({ title, children, defaultOpen = false, meta }: AccordionItemProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="border-b border-line-subtle last:border-b-0">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          id={`${id}-trigger`}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-4 py-4 text-left"
        >
          <span className="font-display text-[18px] font-semibold text-ink">{title}</span>
          <span className="flex items-center gap-3">
            {meta}
            <span
              aria-hidden="true"
              className={cx(
                'text-accent transition-transform duration-200',
                open && 'rotate-45',
              )}
            >
              +
            </span>
          </span>
        </button>
      </h3>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-trigger`}
        hidden={!open}
        className="pb-4 text-[14.5px] leading-relaxed text-ink-muted"
      >
        {children}
      </div>
    </div>
  );
}

export function Accordion({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('rounded-xl border border-line-subtle bg-surface px-5 shadow-card', className)}>
      {children}
    </div>
  );
}
